import { T } from 'gt-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import FileUploadDropzone from '@/components/documents/FileUploadDropzone';
import { useUploadThreadEvidence } from './useUploadThreadEvidence';

interface Props {
  threadId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Modal that uploads one or more documents and files each as evidence on the
 * collection thread — a thin shell around useUploadThreadEvidence, which owns
 * the whole pipeline (the always-on page dropzone shares it). Supports
 * drag-and-drop and click-to-browse via the shared FileUploadDropzone.
 */
export function CollectionThreadDocumentUploadDialog({
  threadId,
  open,
  onOpenChange,
}: Props) {
  const { uploadFiles, progress, isUploading, resetProgress } =
    useUploadThreadEvidence(threadId);

  function handleFilesSelected(files: File[]) {
    void uploadFiles(files);
  }

  function handleOpenChange(next: boolean) {
    onOpenChange(next);
    if (!next) resetProgress();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title, upload documents as thread evidence">
              Upload documents
            </T>
          </DialogTitle>
          <DialogDescription>
            <T context="dialog help, what uploading does">
              Drag and drop files or browse to upload. Each document is filed as
              evidence on this collection thread; email files (.eml, .msg) are
              filed as emails.
            </T>
          </DialogDescription>
        </DialogHeader>
        <FileUploadDropzone
          onFilesSelected={handleFilesSelected}
          uploadProgress={progress}
          isUploading={isUploading}
        />
      </DialogContent>
    </Dialog>
  );
}
